import React from "react"
import cx from "classnames"

// =============================================================================
// Props
// =============================================================================

export type PageSecondaryHeaderActionsProps = {
  children?: React.ReactNode
  className?: string
}

export type PageSecondaryHeaderActionsSlotProps = {
  children?: React.ReactNode
  className?: string
}

// =============================================================================
// Styles
// =============================================================================

const pageSecondaryHeaderSecondaryActionsStyles = [
  "row-start-2 col-span-full flex items-center gap-2 @3xl/page-header:row-start-1 @3xl/page-header:col-span-1 @3xl/page-header:col-start-3 @3xl/page-header:justify-end"
]

// Use `contents` to allow children to participate in the parent flex layout.
// Identical to passing actions directly into `PageSecondaryHeader.Actions`.

const pageSecondaryHeaderActionsCompactStyles =
  "contents @3xl/page-header:hidden"
const pageSecondaryHeaderActionsFullStyles = "hidden @3xl/page-header:contents"

// =============================================================================
// Slots
// =============================================================================

/**
 * Compact actions shown until `@4xl/page-header`.
 */
function PageSecondaryHeaderActionsCompact({
  children,
  className
}: PageSecondaryHeaderActionsSlotProps) {
  return (
    <div
      className={cx(pageSecondaryHeaderActionsCompactStyles, className)}
      data-slot="secondary-actions-compact"
    >
      {children}
    </div>
  )
}

PageSecondaryHeaderActionsCompact.displayName =
  "PageSecondaryHeader.Actions.Compact"

/**
 * Full actions shown above `@4xl/page-header`.
 */
function PageSecondaryHeaderActionsFull({
  children,
  className
}: PageSecondaryHeaderActionsSlotProps) {
  return (
    <div
      className={cx(pageSecondaryHeaderActionsFullStyles, className)}
      data-slot="secondary-actions-full"
    >
      {children}
    </div>
  )
}

PageSecondaryHeaderActionsFull.displayName = "PageSecondaryHeader.Actions.Full"

// =============================================================================
// Component
// =============================================================================

interface PageSecondaryHeaderActionsComponent {
  (props: PageSecondaryHeaderActionsProps): React.JSX.Element
  /** Actions shown until `@4xl/page-header`. */
  Compact: typeof PageSecondaryHeaderActionsCompact
  /** Actions shown above `@4xl/page-header`. */
  Full: typeof PageSecondaryHeaderActionsFull
}

/**
 * Secondary page header actions.
 *
 * Compose `PageSecondaryHeader.Actions.Compact` (shown until `@4xl/page-header`) and
 * `PageSecondaryHeader.Actions.Full` (shown above `@4xl/page-header`) to render different actions
 * to prevent unwanted content wrapping.
 *
 * @example
 * <PageSecondaryHeader.Actions>
 *   <PageSecondaryHeader.Actions.Compact>
 *     <Button variant="secondary" onClick={onArchive}>
 *       <Icon name="archive" />
 *       <VisuallyHidden>Archive</VisuallyHidden>
 *     </Button>
 *     <Button onClick={onCreate}>
 *       <Icon name="plus" />
 *       <VisuallyHidden>New snippet</VisuallyHidden>
 *     </Button>
 *   </PageSecondaryHeader.Actions.Compact>
 *   <PageSecondaryHeader.Actions.Full>
 *     <Button variant="secondary" onClick={onArchive}>
 *       <Icon name="archive" data-icon="inline-start" />
 *       Archive
 *     </Button>
 *     <Button onClick={onCreate}>
 *       <Icon name="plus" data-icon="inline-start" />
 *       New snippet
 *     </Button>
 *   </PageSecondaryHeader.Actions.Full>
 * </PageSecondaryHeader.Actions>
 */
function PageSecondaryHeaderActionsRoot({
  children,
  className
}: PageSecondaryHeaderActionsProps) {
  return (
    <div
      className={cx(pageSecondaryHeaderSecondaryActionsStyles, className)}
      data-slot="secondary-actions"
    >
      {children}
    </div>
  )
}

export const PageSecondaryHeaderActions: PageSecondaryHeaderActionsComponent =
  Object.assign(PageSecondaryHeaderActionsRoot, {
    Compact: PageSecondaryHeaderActionsCompact,
    Full: PageSecondaryHeaderActionsFull
  } as const)
